import { LanguageType } from 'types';
import { ImportMode, MappedCodelist } from '../ImportCodelistPage';
import { useLanguage } from 'features/hooks/useLanguage';
import { CODELIST_SIDEBAR_DICTIONARY } from 'features/codelist/sidebar/Dictionary';
import {
	CodelistRecord,
	CodelistRecordKey,
	codelistRecordKeys,
	createCodelistRecordSchema
} from 'features/codelist/domain/codelistRecordSchema';
import { Button } from 'features/components/Button';
import { useState } from 'react';
import {
	useImportBulkCodelistRecordsMutation,
	usePartialImportCodelistRecordsMutation
} from '../data/useImportBulkCodelistRecordsMutation';
import { useProjectId } from 'features/hooks/useProjectId';
import { ArrowRight } from 'lucide-react';

export const ConfirmFields = ({
	mappedCodelist,
	importMode,
	onBackClicked,
	onImportCompleted,
	rows
}: {
	mappedCodelist: MappedCodelist;
	importMode: ImportMode;
	onBackClicked: () => void;
	onImportCompleted: ({
		importedCount,
		skippedCount
	}: {
		importedCount: number;
		skippedCount: number;
	}) => void;
	rows: Record<string, string>[];
}) => {
	const projectId = useProjectId();

	const language = useLanguage();
	const translation = DICTIONARY[language];
	const codelistTranslation = CODELIST_SIDEBAR_DICTIONARY[language];

	const [invalidRows, setInvalidRows] = useState<Record<string, string>[]>([]);

	const importBulkCodelistRecordsMutation = useImportBulkCodelistRecordsMutation();
	const partialImportCodelistRecordsMutation = usePartialImportCodelistRecordsMutation();

	const mutation =
		importMode === 'full'
			? importBulkCodelistRecordsMutation
			: partialImportCodelistRecordsMutation;

	const validateRows = async () => {
		const invalidRows = [];

		for (const row of rows) {
			const validated = createCodelistRecordSchema(language).safeParse(row);

			if (!validated.success) {
				invalidRows.push(row);
			}
		}

		if (invalidRows.length > 0) {
			setInvalidRows(invalidRows);
			return;
		}

		const { importedCount, skippedCount } = await mutation.mutateAsync({
			identifyingVariableName: mappedCodelist.entryIdentifyingVariable.name,
			records: mappedCodelist.parsedRecords,
			projectId
		});

		onImportCompleted({ importedCount, skippedCount });
	};

	return (
		<div>
			<h2 className="text-2xl font-bold">{translation.title}</h2>

			<p className="text-base mt-4">{translation.description}</p>

			<div className="mt-4">
				<h3 className="text-base font-bold">{codelistTranslation.customId.label}</h3>

				<div className="mt-4 flex gap-2 items-center">
					<p className="text-base">{mappedCodelist.entryIdentifyingVariable.label}</p>

					<ArrowRight className="text-icon-base" />

					<p className="text-base">{mappedCodelist.codelistIdentifyingRowName}</p>
				</div>
			</div>

			{invalidRows.length > 0 && (
				<div className="bg-error-700/25 rounded-md px-6 py-2 mt-4">
					<h3 className="text-lg text-error-700 font-bold">{translation.invalidRows}</h3>
				</div>
			)}

			<div className="mt-10">
				<table>
					<thead>
						<tr className="text-base text-start">
							<th className="pr-10">{translation.columnInImportedFile}</th>
							<th>{translation.fieldInLedidi}</th>
						</tr>
					</thead>

					<tbody>
						{Object.entries(mappedCodelist.fieldMapping).map(([key, value]) => {
							if (!isCodelistRecordKey(key)) {
								console.error(`Invalid codelist record key: ${key}`);
								return null;
							}

							return (
								<tr className="text-base text-start" key={key}>
									<td>{value}</td>
									<td>{codelistTranslation[key].label}</td>
								</tr>
							);
						})}
					</tbody>
				</table>
			</div>

			{mutation.error && (
				<p className="text-error-700 mt-4 text-base font-bold">{translation.importError}</p>
			)}

			<div className="mt-4 flex gap-4">
				<Button variant="secondary" text={translation.back} onClick={onBackClicked} />
				<Button
					text={translation.confirm}
					onClick={validateRows}
					loading={mutation.isLoading}
				/>
			</div>
		</div>
	);
};

const isCodelistRecordKey = (key: string): key is CodelistRecordKey => {
	return codelistRecordKeys.includes(key as CodelistRecordKey);
};

const DICTIONARY = {
	[LanguageType.English]: {
		title: 'Confirm fields',
		description:
			'Please confirm the fields that will be imported. Fields that are not shown here will not be imported.',

		invalidRows: 'Invalid rows. Ensure all rows are valid before uploading the file.',

		columnInImportedFile: 'Column in imported file',
		fieldInLedidi: 'Field in Ledidi',

		back: 'Back',
		confirm: 'Start import',

		importError:
			'Could not import codelist, please try again. If the problem persists, contact support.'
	},

	[LanguageType.Norwegian]: {
		title: 'Bekreft felter',
		description:
			'Bekreft feltene som skal importeres. Felter som ikke vises her blir ikke importert. ',

		invalidRows: 'Ugyldige rader. Sørg for at alle rader er gyldige før du laster opp filen.',

		columnInImportedFile: 'Kolonne i importert fil',
		fieldInLedidi: 'Felt i Ledidi',

		back: 'Tilbake',
		confirm: 'Start import',

		importError:
			'Kunne ikke importere kodeliste, vennligst prøv igjen. Hvis problemet vedvarer, kontakt support.'
	}
} as const;

export const createCodelistRecordsFromMappedCodelist = (args: {
	row: Record<string, string>;
	mappedCodelist: MappedCodelist;
}) => {
	const record: CodelistRecord = {
		dateOfBirth: undefined,
		customId: args.row[args.mappedCodelist.codelistIdentifyingRowName]
	};

	for (const key of Object.keys(args.mappedCodelist.fieldMapping)) {
		if (isCodelistRecordKey(key)) {
			if (key === 'customId') {
				continue;
			}

			record[key] = args.row[args.mappedCodelist.fieldMapping[key]];
		}
	}

	return record;
};
